import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  X,
  Mail,
  Phone,
  User,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  FileText,
  MessageSquare,
  Download,
  Loader2,
  MapPin,
  Pencil,
} from "lucide-react";
import toast from "react-hot-toast";
import { formatDate, formatDateTime } from "../../utils/format";
import waiversService from "../../api/services/waivers.service";

const TONE_STYLES = {
  success: "text-green-600",
  danger: "text-red-600",
  neutral: "text-gray-600",
};

/**
 * The three waiver states are deliberately distinct. "Outdated" means the
 * parent DID sign, just an older version of the document — rendering that as
 * "pending" is what made it look like nobody had signed anything.
 */
const WAIVER_STATUS = {
  signed: {
    label: "Signed",
    chip: "bg-green-100 text-green-800",
    Icon: CheckCircle2,
    iconClass: "text-green-600",
  },
  outdated: {
    label: "Outdated",
    chip: "bg-amber-100 text-amber-800",
    Icon: AlertTriangle,
    iconClass: "text-amber-500",
  },
  pending: {
    label: "Not signed",
    chip: "bg-red-100 text-red-700",
    Icon: AlertCircle,
    iconClass: "text-red-500",
  },
};

/**
 * One required waiver with this parent's status. Signed and outdated waivers
 * expand to the signed record (signer, exact timestamp, version, record id)
 * so an admin can evidence the signature from the profile.
 */
function WaiverRow({ waiver, signerEmail }) {
  const [showRecord, setShowRecord] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const meta = WAIVER_STATUS[waiver.status] || WAIVER_STATUS.pending;
  const Icon = meta.Icon;
  const hasRecord = Boolean(waiver.signed_at || waiver.signer_name);

  // The signed document: the wording of the version that was agreed, plus
  // who agreed and when. Staff need to be able to hand this to someone.
  const handleDownload = async () => {
    if (!waiver.acceptance_id || downloading) return;
    try {
      setDownloading(true);
      const blob = await waiversService.downloadSignedPdf(waiver.acceptance_id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(waiver.name || "waiver").replace(/[^a-z0-9]+/gi, "_")}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download signed waiver:", error);
      toast.error("Could not download that document. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-start gap-2.5">
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${meta.iconClass}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#173151] font-manrope">
          {waiver.name || "Waiver"}
        </p>
        {hasRecord ? (
          <div className="mt-1 flex items-center gap-2">
            <div className="flex-1 min-w-0 rounded-lg border border-gray-200 bg-white px-3 py-2">
              <p className="text-xs text-gray-700 font-manrope flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                Agreed
                {waiver.signed_at ? ` ${formatDateTime(waiver.signed_at)}` : ""}
              </p>
              <p className="text-xs text-gray-500 font-manrope truncate">
                {signerEmail || waiver.signer_name || "—"}
              </p>
            </div>
            {waiver.acceptance_id && (
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                title="Download the signed document"
                aria-label={`Download signed ${waiver.name || "waiver"}`}
                className="shrink-0 rounded-lg border border-gray-200 bg-white p-2 text-[#173151] hover:bg-gray-50 disabled:opacity-50"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        ) : (
          <p className="text-xs text-gray-400 font-manrope">Never signed</p>
        )}
        {waiver.status === "outdated" && (
          <p className="text-xs text-amber-600 font-manrope">
            Signed v{waiver.signed_version ?? "?"} · current is v
            {waiver.current_version ?? "?"} — needs re-signing
          </p>
        )}
        {hasRecord && (
          <>
            <button
              type="button"
              onClick={() => setShowRecord((v) => !v)}
              className="mt-1 text-xs font-semibold text-[#173151] underline font-manrope"
            >
              {showRecord ? "Hide signed record" : "View signed record"}
            </button>
            {showRecord && (
              <div className="mt-2 rounded-xl bg-[#f6f8fb] p-3 space-y-1 text-xs text-gray-600 font-manrope">
                <p>
                  <span className="text-gray-400">Signer: </span>
                  {waiver.signer_name || "—"}
                </p>
                <p>
                  <span className="text-gray-400">Signed: </span>
                  {waiver.signed_at ? formatDateTime(waiver.signed_at) : "—"}
                </p>
                <p>
                  <span className="text-gray-400">Version: </span>v
                  {waiver.signed_version ?? "?"} (current v
                  {waiver.current_version ?? "?"})
                </p>
                {waiver.acceptance_id && (
                  <p className="break-all">
                    <span className="text-gray-400">Record ID: </span>
                    {waiver.acceptance_id}
                  </p>
                )}
                {waiver.signer_name && (
                  <Link
                    to={`/admin/waiver-reports?search=${encodeURIComponent(
                      waiver.signer_name
                    )}`}
                    className="inline-block pt-1 font-semibold text-[#173151] underline"
                  >
                    Open in waiver reports
                  </Link>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <span
        className={`px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 ${meta.chip}`}
      >
        {meta.label}
      </span>
    </div>
  );
}

/**
 * Right-side Client Details drawer: contact info, children with their active
 * enrollments, and — for owners — an outstanding balance card and recent
 * payment/invoice activity. "Send Message" opens the Mass Email composer
 * pre-addressed to this client (the app's standard email flow).
 */
export default function ClientDetailsPanel({
  open,
  loading,
  client,
  details,
  canSeeFinancials,
  onClose,
  onSendMessage,
  onViewInvoices,
  onEdit,
}) {
  if (!open) return null;

  const balance = details?.balance;
  const activity = details?.recent_activity || [];
  // Older cached responses may not carry waivers at all — absent data must not
  // read as non-compliance, so fall back to a quiet empty state.
  const waivers = Array.isArray(details?.waivers) ? details.waivers : [];
  const waiversSigned =
    typeof details?.waivers_signed === "number"
      ? details.waivers_signed
      : waivers.filter((w) => w.status === "signed").length;
  const waiversOutstanding =
    typeof details?.waivers_outstanding === "number"
      ? details.waivers_outstanding
      : waivers.length - waiversSigned;
  const enrollmentsByChild = {};
  (details?.active_enrollments || []).forEach((e) => {
    (enrollmentsByChild[e.child_id || e.child_name] ||= []).push(e);
  });

  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-[#f6f8fb] z-50 shadow-2xl flex flex-col">
        <div className="flex items-start justify-between p-5 border-b border-border-light bg-white/70">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#173151]/10 flex items-center justify-center shrink-0">
              <User className="w-6 h-6 text-[#173151]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[#173151] font-kollektif">
                {client
                  ? `${client.first_name} ${client.last_name}`.trim()
                  : "Client Details"}
              </h2>
              <span
                className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                  client?.status === "active"
                    ? "bg-green-100 text-green-800"
                    : client?.status === "payment_due"
                      ? "bg-red-100 text-red-700"
                      : "bg-gray-100 text-gray-600"
                }`}
              >
                {client?.status === "payment_due"
                  ? "Payment due"
                  : client?.status === "active"
                    ? "Active"
                    : "Inactive"}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100"
            type="button"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {loading || !details ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-btn-gold"></div>
            </div>
          ) : (
            <>
              {/* Contact */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500 font-manrope">
                    Contact information
                  </p>
                  {onEdit && (
                    <button
                      type="button"
                      onClick={onEdit}
                      className="flex items-center gap-1 text-xs font-semibold text-[#173151] hover:underline font-manrope"
                    >
                      <Pencil className="w-3 h-3" /> Edit details
                    </button>
                  )}
                </div>
                <div className="bg-white/80 rounded-2xl p-4 shadow-sm space-y-2 text-sm font-manrope">
                  <p className="flex items-center gap-2 text-[#173151]">
                    <Mail className="w-4 h-4 text-gray-400" /> {details.email}
                  </p>
                  {details.phone && (
                    <p className="flex items-center gap-2 text-[#173151]">
                      <Phone className="w-4 h-4 text-gray-400" /> {details.phone}
                    </p>
                  )}
                  {(details.address || details.city) && (
                    <div className="flex items-start gap-2 text-[#173151]">
                      <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        {details.address && <p>{details.address}</p>}
                        <p>
                          {[details.city, [details.state, details.zip_code].filter(Boolean).join(" ")]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                      </div>
                    </div>
                  )}
                  {(details.additional_emails || []).length > 0 && (
                    <div className="pt-1">
                      <p className="text-xs text-gray-500">Also copied on emails</p>
                      {details.additional_emails.map((email) => (
                        <p key={email} className="flex items-center gap-2 text-[#173151]">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span>{email}</span>
                        </p>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Client since {formatDate(details.created_at)}
                  </p>
                </div>
              </div>

              {/* Children */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-gray-500 font-manrope mb-2">
                  Children ({(details.children || []).length})
                </p>
                <div className="bg-white/80 rounded-2xl p-4 shadow-sm space-y-3">
                  {(details.children || []).length === 0 ? (
                    <p className="text-sm text-gray-500 font-manrope">
                      No children on file.
                    </p>
                  ) : (
                    details.children.map((ch) => {
                      const name = `${ch.first_name} ${ch.last_name}`;
                      const enrs =
                        enrollmentsByChild[ch.id] ||
                        enrollmentsByChild[name] ||
                        [];
                      return (
                        <div key={ch.id} className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-full bg-[#F3BC48]/20 flex items-center justify-center shrink-0 text-xs font-bold text-[#173151]">
                            {(ch.first_name?.[0] || "") + (ch.last_name?.[0] || "")}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-[#173151] font-manrope">
                              {name}
                            </p>
                            {enrs.length > 0 ? (
                              enrs.map((e) => (
                                <p
                                  key={e.id}
                                  className="text-xs text-gray-500 font-manrope truncate"
                                >
                                  {e.class_name}
                                </p>
                              ))
                            ) : (
                              <p className="text-xs text-gray-400 font-manrope">
                                No active enrollment
                              </p>
                            )}
                          </div>
                          {enrs.length > 0 && (
                            <span className="text-xs font-semibold text-green-600 font-manrope shrink-0">
                              Active
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Waivers */}
              <div>
                <div className="flex items-baseline justify-between gap-2 mb-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500 font-manrope">
                    Waivers
                  </p>
                  {waivers.length > 0 && (
                    <p
                      className={`text-xs font-semibold font-manrope ${
                        waiversOutstanding > 0
                          ? "text-amber-600"
                          : "text-green-600"
                      }`}
                    >
                      {waiversSigned} of {waivers.length} signed
                    </p>
                  )}
                </div>
                <div className="bg-white/80 rounded-2xl p-4 shadow-sm space-y-3">
                  {waivers.length === 0 ? (
                    <p className="text-sm text-gray-500 font-manrope">
                      No waiver information available for this client.
                    </p>
                  ) : (
                    waivers.map((w, i) => (
                      <WaiverRow
                        key={w.waiver_template_id || w.name || i}
                        waiver={w}
                        signerEmail={details?.email}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Balance (owner only) */}
              {canSeeFinancials && balance && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500 font-manrope mb-2">
                    Balance
                  </p>
                  <div
                    className={`rounded-2xl p-4 shadow-sm ${
                      balance.amount_due > 0
                        ? "bg-red-50 border border-red-100"
                        : "bg-green-50 border border-green-100"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <p
                        className={`font-semibold font-manrope ${
                          balance.amount_due > 0 ? "text-red-600" : "text-green-700"
                        }`}
                      >
                        {balance.amount_due > 0 ? "Payment due" : "All paid up"}
                      </p>
                      <p
                        className={`text-xl font-bold font-kollektif ${
                          balance.amount_due > 0 ? "text-red-600" : "text-green-700"
                        }`}
                      >
                        ${Number(balance.amount_due || 0).toFixed(2)}
                      </p>
                    </div>
                    {balance.due_date && balance.amount_due > 0 && (
                      <p className="text-xs text-gray-500 font-manrope mt-1">
                        Due date: {formatDate(balance.due_date)} ·{" "}
                        {balance.open_invoices} open invoice
                        {balance.open_invoices === 1 ? "" : "s"}
                      </p>
                    )}
                    {balance.amount_due > 0 && (
                      <button
                        type="button"
                        onClick={onViewInvoices}
                        className="mt-3 w-full px-4 py-2 rounded-lg bg-btn-gold text-[#173151] text-sm font-semibold font-manrope hover:opacity-90"
                      >
                        View Invoices
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Recent activity (owner only) */}
              {canSeeFinancials && activity.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-gray-500 font-manrope mb-2">
                    Recent activity
                  </p>
                  <div className="bg-white/80 rounded-2xl p-4 shadow-sm space-y-3">
                    {activity.map((a, i) => (
                      <div key={i} className="flex items-start gap-2.5">
                        {a.tone === "success" ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                        ) : a.tone === "danger" ? (
                          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                        ) : (
                          <FileText className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-sm font-semibold font-manrope ${
                              TONE_STYLES[a.tone] || TONE_STYLES.neutral
                            }`}
                          >
                            {a.label}
                          </p>
                          <p className="text-xs text-gray-500 font-manrope truncate">
                            {a.detail}
                          </p>
                        </div>
                        <p className="text-xs text-gray-400 font-manrope shrink-0">
                          {a.date ? formatDate(a.date) : ""}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {canSeeFinancials && details.total_spent > 0 && (
                <p className="text-xs text-gray-500 font-manrope text-center">
                  Lifetime total collected: $
                  {Number(details.total_spent).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </p>
              )}
            </>
          )}
        </div>

        <div className="p-5 border-t border-border-light bg-white/70">
          <button
            type="button"
            onClick={onSendMessage}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#173151] text-white font-semibold font-manrope hover:opacity-90"
          >
            <MessageSquare className="w-4 h-4" /> Send Message
          </button>
        </div>
      </div>
    </>
  );
}
